import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import SectionHeading from "../components/blocks/SectionHeading";
import CTASection from "../components/blocks/CTASection";
import { LinkCard, TextCard } from "../components/blocks/Cards";
import { AUDIENCES, DOMAINS, PHILOSOPHY_CHAIN } from "../data/content";

export default function LearningPage() {
  return (
    <>
      <SEO
        title="Learn — Learning Paths by Audience | The Vantage Institute™"
        description="Find your learning path at The Vantage Institute: structured development for entrepreneurs, founders, leaders, managers, sales professionals, HR/L&D leaders, young professionals and organisations."
        path="/learning"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Learn" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Learn at Vantage</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Find your learning path.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            Learning works when it starts from who you are and what you are responsible for. Choose your audience
            below — each path leads to the capability domains and programmes built for your stage.
          </p>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="learning-audiences">
        <SectionHeading eyebrow="Learning Paths" title="Who are you building for?" />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {AUDIENCES.map((a) => (
            <LinkCard
              key={a.title}
              title={a.title}
              text={a.text}
              to={a.to || "/programmes"}
              linkLabel={a.to ? "For Organisations" : "View Programmes"}
              testId={`path-${a.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            />
          ))}
        </div>
      </section>

      <section className="bg-paper" data-testid="learning-domains">
        <div className="container-x py-16 sm:py-24">
          <SectionHeading
            eyebrow="What You Can Develop"
            title="Eight capability domains."
            intro="Every Vantage programme maps to at least one of these domains — the full arc of modern business capability."
          />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {DOMAINS.map((d) => (
              <LinkCard key={d.title} title={d.title} text={d.text} to="/programmes" linkLabel="View Programmes" testId={`learn-domain-${d.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
            ))}
          </div>
        </div>
      </section>

      <section className="container-x py-16 sm:py-24" data-testid="learning-how">
        <div className="grid gap-12 lg:grid-cols-2">
          <SectionHeading
            eyebrow="How Vantage Learning Works"
            title="Structured for application, not attendance."
            intro="Vantage learning follows the Institute's philosophy chain — from experience to business impact. Frameworks give structure to thinking; application turns thinking into capability; capability, used consistently, becomes performance."
          />
          <ol className="space-y-0 border-l-2 border-gold">
            {PHILOSOPHY_CHAIN.map((step, i) => (
              <li key={step} className="relative py-3 pl-6">
                <span className="absolute -left-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full bg-gold" aria-hidden="true" />
                <p className="font-sub text-sm font-semibold uppercase tracking-[0.1em] text-navy">
                  <span className="mr-2 text-gold-deep">{String(i + 1).padStart(2, "0")}</span>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-3">
          <TextCard title="Frameworks" text="Every programme is anchored in the Vantage Thinking Toolbox™ — proprietary frameworks that structure judgement." />
          <TextCard title="Live Application" text="Participants work on their own real business situations, so learning transfers directly into decisions." />
          <LinkCard title="Capability Assessment" text="Not sure where to start? Take the short Vantage capability assessment and get a recommended learning path." to="/assessment" linkLabel="Take the Assessment" testId="learning-assessment-card" />
        </div>
      </section>

      <CTASection
        title="Ready to build your next capability?"
        primaryLabel="Explore Programmes"
        primaryTo="/programmes"
        secondaryLabel="Talk to Vantage"
        secondaryTo="/contact"
        testId="learning-cta"
      />
    </>
  );
}
